import { useState, useMemo } from 'react';
import { useDataProvider, useNotify, useRedirect, useGetList } from 'react-admin';
import authFetch from '../utils/authFetch';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Stack, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, Switch,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { card, btn, inputSx, NAVY, GOLD } from '../styles/ds';

const ProductCreate = () => {
  const dp       = useDataProvider();
  const notify   = useNotify();
  const redirect = useRedirect();

  const [name, setName]                         = useState('');
  const [description, setDescription]           = useState('');
  const [isFinishedProduct, setIsFinishedProduct] = useState(false);
  const [unit, setUnit]                         = useState('יחידה');
  const [selected, setSelected]                 = useState(new Set()); // Set of attr.key strings
  const [saving, setSaving]                     = useState(false);

  // Fetch all existing attributes to use as templates
  const { data: allAttributes = [], isLoading } = useGetList('attributes', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'key', order: 'ASC' },
  });

  // Deduplicate by key — one template per attribute type
  const templates = useMemo(() => {
    const seen = new Set();
    return allAttributes.filter(attr => {
      if (seen.has(attr.key)) return false;
      seen.add(attr.key);
      return true;
    });
  }, [allAttributes]);

  const toggle = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) { notify('נא להזין שם קטגוריה', { type: 'warning' }); return; }
    setSaving(true);
    try {
      // 1. Create the product
      const { data: product } = await dp.create('products', {
        data: { name: name.trim(), description: description.trim() || null, is_finished_product: isFinishedProduct, unit },
      });

      // 2. Copy selected attribute templates (non-blocking — failures show a warning)
      const selectedIds = templates.filter(t => selected.has(t.key)).map(t => t.id);
      if (selectedIds.length > 0) {
        try {
          const res = await authFetch('/attributes/product-links', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ product_id: product.id, attribute_ids: selectedIds }),
          });
          if (!res.ok) throw new Error();
          notify('הקטגוריה נוצרה בהצלחה עם כל התכונות', { type: 'success' });
        } catch {
          notify('הקטגוריה נוצרה, אך שיוך התכונות נכשל', { type: 'warning' });
        }
      } else {
        notify('הקטגוריה נוצרה בהצלחה', { type: 'success' });
      }

      redirect(`/products/${product.id}/show`);
    } catch (e) {
      console.error('Create product error:', e);
      notify(`שגיאה: ${e?.message || 'שגיאה ביצירת הקטגוריה'}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="crm-page" p={3} sx={{ maxWidth: 720 }}>
      <Typography variant="h5" fontWeight={700} color="primary.main" mb={3}>
        קטגוריה חדשה
      </Typography>

      {/* ── Basic info ────────────────────────────────────────────── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Typography fontWeight={700} color="primary.main" mb={2}>פרטי הקטגוריה</Typography>
          <Stack spacing={2}>
            <TextField
              label="שם הקטגוריה"
              value={name}
              onChange={e => setName(e.target.value)}
              fullWidth
              sx={inputSx}
              placeholder='למשל: תפילין, מזוזות, ספרי תורה'
            />
            <TextField
              label="תיאור (אופציונלי)"
              value={description}
              onChange={e => setDescription(e.target.value)}
              fullWidth
              sx={inputSx}
              multiline
              rows={2}
            />
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>יחידת מידה</InputLabel>
              <Select value={unit} label="יחידת מידה" onChange={e => setUnit(e.target.value)}>
                {['יחידה','מטר','ס"מ','ק"ג','גרם','ליטר','זוג','סט'].map(u =>
                  <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={isFinishedProduct}
                  onChange={e => setIsFinishedProduct(e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: NAVY }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: NAVY } }}
                />
              }
              label={
                <Box>
                  <Typography fontWeight={600} fontSize={14} color={NAVY}>קטגוריה זו היא מוצר מוגמר</Typography>
                  <Typography variant="caption" color="text.secondary">מוצרים מהקטגוריה יופיעו כאפשרות בייצור ומנות</Typography>
                </Box>
              }
            />
          </Stack>
        </CardContent>
      </Card>

      {/* ── Attribute templates ───────────────────────────────────── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Typography fontWeight={700} color="primary.main" mb={0.5}>
            תכונות
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            בחר תכונות מהרשימה הקיימת — הן יועתקו לקטגוריה החדשה.
            ניתן לערוך ולהוסיף תכונות גם לאחר היצירה.
          </Typography>

          {isLoading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} />
              <Typography fontSize={13} color="text.secondary">טוען תכונות...</Typography>
            </Box>
          ) : templates.length === 0 ? (
            <Typography color="text.secondary" fontSize={13}>
              אין תכונות קיימות — ניתן להוסיף תכונות לאחר יצירת הקטגוריה.
            </Typography>
          ) : (
            <>
              <Box className="crm-form-grid" sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 1.5,
                mb: 1.5,
              }}>
                {templates.map(attr => {
                  const isSelected = selected.has(attr.key);
                  return (
                    <Box
                      key={attr.key}
                      onClick={() => toggle(attr.key)}
                      sx={{
                        border: `2px solid ${isSelected ? NAVY : 'rgba(0,0,0,0.1)'}`,
                        borderRadius: 2,
                        p: 1.5,
                        cursor: 'pointer',
                        backgroundColor: isSelected ? 'rgba(13,29,62,0.04)' : '#fff',
                        transition: '0.15s',
                        userSelect: 'none',
                        '&:hover': {
                          borderColor: isSelected ? NAVY : 'rgba(13,29,62,0.35)',
                          backgroundColor: isSelected ? 'rgba(13,29,62,0.06)' : 'rgba(13,29,62,0.02)',
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Checkbox
                          checked={isSelected}
                          size="small"
                          onClick={e => e.stopPropagation()}
                          onChange={() => toggle(attr.key)}
                          sx={{
                            p: 0, mt: 0.2, flexShrink: 0,
                            color: NAVY,
                            '&.Mui-checked': { color: NAVY },
                          }}
                        />
                        <Box minWidth={0}>
                          <Typography fontWeight={700} fontSize={14} color={NAVY}>
                            {attr.label || attr.key}
                          </Typography>
                          <Typography
                            fontSize={11}
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace', mb: 0.8 }}
                          >
                            key: {attr.key}
                          </Typography>
                          {attr.options?.length > 0 && (
                            <Stack className="crm-wrap" direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                              {attr.options.slice(0, 4).map(opt => (
                                <Chip
                                  key={opt}
                                  label={opt}
                                  size="small"
                                  sx={{
                                    fontSize: 10,
                                    height: 18,
                                    backgroundColor: isSelected
                                      ? 'rgba(13,29,62,0.1)'
                                      : 'rgba(0,0,0,0.06)',
                                  }}
                                />
                              ))}
                              {attr.options.length > 4 && (
                                <Typography fontSize={10} color="text.secondary" alignSelf="center">
                                  +{attr.options.length - 4}
                                </Typography>
                              )}
                            </Stack>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>

              {selected.size > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {selected.size} תכונות נבחרו
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Actions ───────────────────────────────────────────────── */}
      <Stack className="crm-actions" direction="row" spacing={2} justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={() => redirect('/products')}
          sx={btn.cancel}
        >
          ביטול
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !name.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={btn.primaryLg}
        >
          צור קטגוריה
        </Button>
      </Stack>
    </Box>
  );
};

export default ProductCreate;
